import {
    ColumnDef,
    SortingState,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'

import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/common/ui/table'
import { useState } from 'react'
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react'
import { cn } from '../utils/utils'

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[]
    data: TData[]
    noDataMessage?: string
    classNameHeaderRow?: string
    classNameHeader?: string
    classNameCell?: string
    classNameFooter?: string
    hideFooterOnSmall?: boolean
}

export function DataTable<TData, TValue>({
    columns,
    data,
    noDataMessage = 'No results',
    classNameHeaderRow,
    classNameHeader,
    classNameCell,
    classNameFooter,
    hideFooterOnSmall,
}: DataTableProps<TData, TValue>) {
    const [sorting, setSorting] = useState<SortingState>([])
    const table = useReactTable({
        data,
        columns,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: {
            sorting,
        },
    })

    const hasFooter =
        table
            .getFooterGroups()
            .some((group) =>
                group.headers.some((header) => !header.isPlaceholder && Boolean(header.column.columnDef.footer)),
            ) && data.length > 0

    return (
        <div className="w-full rounded-md overflow-x-auto">
            <Table className="min-w-full">
                <TableHeader className={classNameHeaderRow}>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                const isSortable = header.column.getCanSort()
                                const sortingState = header.column.getIsSorted()

                                return (
                                    <TableHead
                                        key={header.id}
                                        className={`${classNameHeader} ${isSortable ? 'cursor-pointer' : ''} ${(header.column.columnDef as any).meta?.className ?? ''}`}
                                        onClick={isSortable ? header.column.getToggleSortingHandler() : undefined}
                                    >
                                        {header.isPlaceholder ? null : (
                                            <div className="flex items-center">
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {sortingState === 'asc' && <ChevronUp className="w-4 h-4 relative" />}
                                                {sortingState === 'desc' && (
                                                    <ChevronDown className="w-4 h-4 relative" />
                                                )}
                                                {header.column.getCanSort() &&
                                                    sortingState !== 'asc' &&
                                                    sortingState !== 'desc' && (
                                                        <ChevronsUpDown className="w-4 h-4 relative" />
                                                    )}
                                            </div>
                                        )}
                                    </TableHead>
                                )
                            })}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody className="[&_tr:last-child]:border-b">
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell
                                        width={cell.column.getSize()}
                                        className={`py-1 px-4 ${classNameCell} ${(cell.column.columnDef as any).meta?.className ?? ''}`}
                                        key={cell.id}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="h-24 text-center">
                                {noDataMessage}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
                {hasFooter && (
                    <TableFooter
                        className={cn(
                            '[&_tr:last-child]:border-b',
                            hideFooterOnSmall ? 'hidden md:table-footer-group' : '',
                            classNameFooter,
                        )}
                    >
                        {table.getFooterGroups().map((footerGroup) => (
                            <TableRow key={footerGroup.id}>
                                {footerGroup.headers.map((header) => (
                                    <TableCell key={header.id} className={classNameFooter}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.footer, header.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableFooter>
                )}
            </Table>
        </div>
    )
}
